import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAzureConfig, azureAuthCallback, login as apiLogin, getLoginOptions } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function LoginPage() {
    const { t } = useTranslation();
    const { login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [azureConfig, setAzureConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const callbackHandled = useRef(false);

    // Two-step email-first flow: 'email' → enter email; 'method' → show the
    // auth methods enabled for THAT email's organisation (Finding 5.73).
    const [step, setStep] = useState('email');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // Auth methods resolved for the entered email's org. Null until step 2.
    const [options, setOptions] = useState(null);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [optionsError, setOptionsError] = useState(null);
    const [emailLoading, setEmailLoading] = useState(false);

    useEffect(() => {
        // Azure/MS365 platform config (client id, tenant) — independent of the
        // per-org gating. Determines whether the SSO button can actually launch.
        const fetchAzureConfig = async () => {
            try {
                const response = await getAzureConfig();
                if (response.data?.enabled) {
                    setAzureConfig(response.data);
                }
            } catch {
                // Azure not configured — SSO button stays disabled.
            }
        };

        fetchAzureConfig();
    }, []);

    useEffect(() => {
        const handleAzureCallback = async (code) => {
            setLoading(true);
            try {
                const redirectUri = window.location.origin + '/login';
                const response = await azureAuthCallback({ code, redirect_uri: redirectUri });
                login(response.data.access_token, response.data.user);
                toast.success(t('auth.login.success_toast'));
                navigate('/dashboard');
            } catch (error) {
                toast.error(error.response?.data?.detail || t('auth.login.failed_toast'));
                window.history.replaceState({}, document.title, '/login');
            } finally {
                setLoading(false);
            }
        };

        const code = searchParams.get('code');
        if (code && !callbackHandled.current) {
            callbackHandled.current = true;
            window.history.replaceState({}, document.title, '/login');
            handleAzureCallback(code);
        }
    }, [searchParams, login, navigate, t]);

    const handleAzureLogin = () => {
        if (!azureConfig) return;

        const redirectUri = window.location.origin + '/login';
        const authUrl =
            `${azureConfig.authority}/oauth2/v2.0/authorize?` +
            `client_id=${azureConfig.client_id}` +
            `&response_type=code` +
            `&redirect_uri=${encodeURIComponent(redirectUri)}` +
            `&response_mode=query` +
            `&scope=${encodeURIComponent('openid profile email User.Read')}` +
            `&state=azure_login`;

        window.location.href = authUrl;
    };

    // Step 1 → resolve which auth methods the entered email's org enables.
    const handleContinue = async (e) => {
        e.preventDefault();
        const trimmed = email.trim();
        if (!trimmed) return;
        setOptionsError(null);
        setOptionsLoading(true);
        try {
            const res = await getLoginOptions(trimmed);
            setOptions(res.data);
            setStep('method');
        } catch (err) {
            // Any failure resolving options — including a 429 from the shared
            // rate-limit bucket — must NEVER lock a legitimate user out of the
            // login page (a tripped limiter would otherwise be a global DoS
            // switch). Fail open to showing both methods; the real /login and
            // SSO endpoints still enforce the actual rules, and a direct
            // attacker still receives throttled 429s with no options data.
            // A 429 gets a non-blocking notice so a genuinely throttled user
            // understands why both methods appeared.
            if (err.response?.status === 429) {
                setOptionsError(t('auth.login.too_many_attempts'));
            }
            setOptions({ email_login_enabled: true, ms365_login_enabled: true });
            setStep('method');
        } finally {
            setOptionsLoading(false);
        }
    };

    const handleBack = () => {
        setStep('email');
        setOptions(null);
        setPassword('');
        setOptionsError(null);
    };

    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setEmailLoading(true);
        try {
            const response = await apiLogin(email.trim(), password);
            login(response.data.access_token, response.data.user);
            toast.success(t('auth.login.success_toast'));
            navigate('/dashboard');
        } catch (err) {
            toast.error(err.response?.data?.detail || t('auth.login.error'));
        } finally {
            setEmailLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen login-bg flex items-center justify-center">
                <p className="text-black uppercase tracking-widest text-sm">
                    {t('auth.login.signing_in_message')}
                </p>
            </div>
        );
    }

    const emailEnabled = !!options?.email_login_enabled;
    const ms365Enabled = !!options?.ms365_login_enabled;

    const inputClass =
        'w-full px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-white/60';

    return (
        <div className="min-h-screen login-bg" data-testid="login-page">
            <div className="min-h-screen login-overlay flex items-center justify-center p-4">
                <div className="w-full max-w-md text-center">
                    <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/company/logo`}
                        onError={(e) => { e.currentTarget.src = '/hisland-logo.png'; e.currentTarget.onerror = null; }}
                        alt="Company logo"
                        className="h-10 mx-auto mb-8 brightness-0 invert"
                    />

                    <h1 className="text-4xl font-bold text-white mb-2 tracking-tight">
                        {t('common.app_name')}
                    </h1>
                    <p className="text-white/70 text-sm uppercase tracking-widest mb-8">
                        {t('common.app_tagline')}
                    </p>

                    {/* ── Step 1: enter email ─────────────────────────────── */}
                    {step === 'email' && (
                        <>
                            <p className="text-xs text-white/60 mb-6">
                                {t('auth.login.email_prompt')}
                            </p>
                            <form onSubmit={handleContinue} className="space-y-3 text-left" data-testid="email-continue-form">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder={t('auth.login.email_placeholder')}
                                    required
                                    autoFocus
                                    autoComplete="username"
                                    className={inputClass}
                                    data-testid="email-input"
                                />
                                <Button
                                    type="submit"
                                    disabled={optionsLoading}
                                    className="w-full h-12 uppercase tracking-wider text-xs"
                                    data-testid="continue-btn"
                                >
                                    {optionsLoading ? t('auth.login.checking') : t('auth.login.continue')}
                                </Button>
                            </form>
                            {optionsError && (
                                <p className="text-xs text-red-300 mt-4" data-testid="options-error">
                                    {optionsError}
                                </p>
                            )}
                        </>
                    )}

                    {/* ── Step 2: method(s) for this email's org ──────────── */}
                    {step === 'method' && (
                        <>
                            <p className="text-xs text-white/60 mb-1">
                                {t('auth.login.signing_in_as', { email: email.trim() })}
                            </p>
                            {optionsError && (
                                <p className="text-xs text-amber-300 mb-2" data-testid="options-error-method">
                                    {optionsError}
                                </p>
                            )}
                            <button
                                type="button"
                                onClick={handleBack}
                                className="text-white/40 text-xs uppercase tracking-wider hover:text-white/70 mb-6"
                                data-testid="different-email-btn"
                            >
                                {t('auth.login.different_email')}
                            </button>

                            {/* Email/password — only when this org enables it */}
                            {emailEnabled && (
                                <form onSubmit={handleEmailLogin} className="space-y-3 text-left" data-testid="email-login-form">
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        placeholder={t('auth.login.password_placeholder')}
                                        required
                                        autoFocus
                                        autoComplete="current-password"
                                        className={inputClass}
                                        data-testid="password-input"
                                    />
                                    <Button
                                        type="submit"
                                        disabled={emailLoading}
                                        className="w-full h-12 uppercase tracking-wider text-xs"
                                        data-testid="email-login-submit-btn"
                                    >
                                        {emailLoading ? t('auth.login.signing_in') : t('auth.login.sign_in_email')}
                                    </Button>
                                </form>
                            )}

                            {/* Divider — only when both methods are offered */}
                            {emailEnabled && ms365Enabled && (
                                <div className="flex items-center gap-3 my-4">
                                    <div className="flex-1 h-px bg-white/20" />
                                    <span className="text-white/40 text-xs uppercase tracking-widest">
                                        {t('auth.login.or')}
                                    </span>
                                    <div className="flex-1 h-px bg-white/20" />
                                </div>
                            )}

                            {/* SSO-only note when email login is disabled for this org */}
                            {!emailEnabled && ms365Enabled && (
                                <p className="text-xs text-white/60 mb-4" data-testid="sso-only-note">
                                    {t('auth.login.sso_only_note')}
                                </p>
                            )}

                            {/* MS365 SSO — only when this org enables it */}
                            {ms365Enabled && (
                                <Button
                                    className="w-full h-12 uppercase tracking-wider text-xs border-2"
                                    variant="outline"
                                    onClick={handleAzureLogin}
                                    disabled={!azureConfig}
                                    data-testid="azure-login-btn"
                                >
                                    <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23">
                                        <path fill="#f25022" d="M0 0h11v11H0z" />
                                        <path fill="#00a4ef" d="M12 0h11v11H12z" />
                                        <path fill="#7fba00" d="M0 12h11v11H0z" />
                                        <path fill="#ffb900" d="M12 12h11v11H12z" />
                                    </svg>
                                    {t('auth.login.button_text')}
                                </Button>
                            )}

                            {/* SSO enabled for the org but Azure not yet configured at platform level */}
                            {ms365Enabled && !azureConfig && (
                                <p className="text-xs text-amber-300 mt-1" data-testid="azure-not-configured-note">
                                    {t('auth.azure.not_configured')}
                                </p>
                            )}

                            {/* No method available for this org (both disabled) */}
                            {!emailEnabled && !ms365Enabled && (
                                <p className="text-xs text-red-300 mt-4" data-testid="no-methods-note">
                                    {t('auth.login.no_methods')}
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
